<div class="flex-column gap-4 animated fadeIn">

	<div class="list-group list-group-flush settings-list-container" *ngIf="mcpServer">
		<!--CC: taken from integrations.component.html -> info-banner (it supplies its own ⓘ, so no icon element is needed)-->
		<div class="info-banner fw-normal lh-base" *ngIf="mcpServer.read_only">
			Your account has read only permissions, so the assistant can search and view your data but cannot change it.
			Write actions (releasing emails, updating allow/block lists, reviewers and settings) are blocked.
		</div>

		<!--Connect a client-->
		<div class="list-group-item settings-list-item">
			<div class="title-section-container flex-column gap-2">
				<b>Connect your assistant</b>
				<span class="lh-base" style="font-size: 0.9em;">Pick the assistant you use and follow the steps. Only accept a connection you started yourself.</span>
			</div>
			<div class="content-container flex-column gap-3">
				<!--CC: taken from user-permissions.component.html - a grid tiles the clients in equal columns instead of a ragged wrapping row-->
				<div class="gap-2" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));">
					<btn-c *ngFor="let client of clients" style="width: 100%;"
						   [startIcon]="client.icon"
						   [variant]="selectedClient?.key === client.key ? '' : 'outlined'"
						   (action)="selectedClient = client;">
						{{client.name}}
					</btn-c>
				</div>
				<div class="flex-column gap-2" *ngIf="selectedClient">
					<ol class="m-0 ps-3 flex-column gap-1 lh-base">
						<li *ngFor="let step of selectedClient.steps">{{step}}</li>
					</ol>
					<div class="flex-row align-items-center gap-2">
						<span style="flex-shrink: 0;">{{selectedClient.commandLabel}}:</span>
						<!--CC: a wrapping code block instead of an input: the CLI commands are longer than the column, so an input cut them off-->
						<code class="f-1-0" style="min-width: 0; white-space: pre-wrap; word-break: break-all;">{{selectedClient.command}}</code>
						<span (click)="copyToClipboard(selectedClient.command, selectedClient.commandLabel);" tooltip="Copy"
							  style="font-size: 11px; margin-left:10px; white-space: nowrap; flex-shrink: 0;" class="hSpace05 copy-link">
							<i class="far fa-copy" style="cursor: pointer;"></i>
							<span>Copy</span>
						</span>
					</div>
				</div>
			</div>
		</div>

		<!--Permissions-->
		<div class="list-group-item settings-list-item">
			<div class="title-section-container flex-column gap-2">
				<b>Permissions</b>
				<span class="lh-base" style="font-size: 0.9em;">What you grant on the consent page when the assistant connects. Clients request both by default; the assistant still asks for your approval before each tool call.</span>
			</div>
			<div class="content-container flex-column gap-2">
				<div class="flex-row align-items-start gap-2" *ngFor="let scope of mcpServer.scopes">
					<code style="flex-shrink: 0; min-width: 70px;">{{scope.scope}}</code>
					<span>{{scope.description}}</span>
				</div>
			</div>
		</div>

		<!--Partners: managed plans-->
		<div class="list-group-item settings-list-item" *ngIf="mcpServer.is_partner">
			<div class="title-section-container flex-column gap-2">
				<b>Managed plans</b>
				<span class="lh-base" style="font-size: 0.9em;">One connection works across the plans you manage, like the multi-tenant view.</span>
			</div>
			<div class="content-container flex-column gap-2 lh-base">
				<span>Ask for your managed plans first ("list my managed plans"), then name the plan by its admin email ("show the allow list of admin@customer.com"). Every tool takes an optional <code>managed_admin</code> parameter that the assistant fills in; without it the tool runs on your own plan.</span>
				<span class="text-muted" style="font-size: 0.9em;">What you may do on a managed plan is what your account may do in the web app, and every action is logged under your partner account.</span>
			</div>
		</div>

		<!--API key alternative-->
		<div class="list-group-item settings-list-item">
			<div class="title-section-container flex-column gap-2">
				<b>Scripts and headless setups</b>
				<span class="lh-base" style="font-size: 0.9em;">Instead of signing in, a script can send your API key and secret on every request.</span>
			</div>
			<div class="content-container flex-column gap-2 lh-base">
				<span>Send the headers <code>x-trustifi-key</code> and <code>x-trustifi-secret</code>, or one header <code>Authorization: Bearer &lt;base64 of key:secret&gt;</code>, over HTTPS only.</span>
				<span class="text-muted" style="font-size: 0.9em;">Prefer sign-in for people: an API key has full access (no read/write split), every call runs as the key's owner and it stays valid until the key is rotated. Never paste the key into a chat or a shared file.</span>
				<div class="flex-row align-items-center gap-2">
					<span *ngIf="mcpServer.api_key_enabled">Your API key is enabled.</span>
					<span *ngIf="!mcpServer.api_key_enabled">Your API key is not enabled.</span>
					<a class="blue-link no-underline" (click)="openApiTab.emit();">Manage it in the API tab</a>
				</div>
			</div>
		</div>

		<!--Tool catalog-->
		<div class="list-group-item settings-list-item">
			<div class="title-section-container flex-column gap-2">
				<b>What the assistant can do</b>
				<span class="lh-base" style="font-size: 0.9em;">{{mcpServer.tools.length}} tools, grouped by product. "Read" tools only search and read; "Write" tools change data and need the write permission.</span>
			</div>
			<div class="content-container flex-column gap-2">
				<div class="flex-column gap-1" *ngFor="let category of toolCategories">
					<div class="flex-row align-items-center gap-2" style="cursor: pointer;" (click)="category.expanded = !category.expanded;">
						<i class="far" [ngClass]="category.expanded ? 'fa-chevron-down' : 'fa-chevron-right'" style="font-size: 0.75rem; width: 12px;"></i>
						<b>{{category.name}}</b>
						<span class="text-muted" style="font-size: 0.9em;">{{category.readCount}} read<span *ngIf="category.writeCount">, {{category.writeCount}} write</span></span>
					</div>
					<div class="flex-column gap-2 ps-4 pb-2 animated fadeIn" *ngIf="category.expanded">
						<div class="flex-column gap-1" *ngFor="let tool of category.tools">
							<div class="flex-row align-items-center gap-2">
								<code>{{tool.name}}</code>
								<span class="badge" [ngClass]="tool.read_only ? 'bg-secondary' : 'bg-warning text-dark'" style="font-weight: 500;">{{tool.read_only ? 'Read' : 'Write'}}</span>
							</div>
							<span class="lh-base text-muted" style="font-size: 0.9em;">{{tool.summary}}</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!--Example prompts-->
		<div class="list-group-item settings-list-item">
			<div class="title-section-container flex-column gap-2">
				<b>Try asking</b>
				<span class="lh-base" style="font-size: 0.9em;">Searches need a time window; the assistant asks for one when you leave it out.</span>
			</div>
			<div class="content-container flex-column gap-1 lh-base">
				<span *ngFor="let prompt of examplePrompts">"{{prompt}}"</span>
			</div>
		</div>
	</div>
</div>
